import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletService } from '../../services/walletService';
import PassCard from './PassCard';
import BackConfigEditor from './BackConfigEditor';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import {
  Smartphone, Bell, Trash2, RefreshCw, Search, Wallet,
  CheckCircle, AlertCircle, Clock, XCircle, ChevronRight,
  Shield, Car, Home, Plane, Heart, Activity, Loader2,
  Copy, Download, RotateCcw, Server,
} from 'lucide-react';
import {
  STATE_CONFIG, ramoLabel, STATUS_OPTIONS,
  fmt, fmtDatetime, StatePill, GlassCard, GlassAura, GlassKpi,
} from './walletShared';

// ─── constants ────────────────────────────────────────────────────────────────

const TYPE_ICON = {
  auto: Car, hogar: Home, viaje: Plane, salud: Heart,
  vida: Shield, soap: Shield, rc: Shield,
};

const LIMIT = 25;

const CUOTA_BADGE = {
  paid:       { label: 'Pagada',     cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  pending:    { label: 'Pendiente',  cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  failed:     { label: 'Fallida',    cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  processing: { label: 'Procesando', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  cancelled:  { label: 'Cancelada',  cls: 'bg-slate-100 text-slate-400 line-through' },
};

function CuotaBadge({ status }) {
  const b = CUOTA_BADGE[status] ?? CUOTA_BADGE.pending;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${b.cls}`}>{b.label}</span>;
}

const cuotaMoney = (c) => {
  if (c?.amount == null) return '—';
  const cur = String(c.currency || '').toUpperCase();
  if (cur === 'UF') return `${Number(c.amount).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} UF`;
  if (cur === 'CLP' || cur === '$' || cur === '') return `$${Math.round(Number(c.amount)).toLocaleString('es-CL')}`;
  return `${Number(c.amount).toLocaleString('es-CL')} ${cur}`;
};

// ─── helpers ──────────────────────────────────────────────────────────────────

// Id canónico del certificado según origen: detalle trae policy.id, la lista
// de /admin/passes trae certificado_id (ex policy_id) y la búsqueda policy_id.
const policyId = (p) => p?.policy?.id ?? p?.policy_id ?? p?.certificado_id;

// ─── component ────────────────────────────────────────────────────────────────

export default function WalletPasses() {
  const { toast } = useToast();
  const navigate = useNavigate();

  // tabla principal
  const [passes, setPasses]             = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [search, setSearch]             = useState('');
  const [typeFilter, setTypeFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]                 = useState(1);

  // ramos
  const [ramos, setRamos] = useState([]);
  useEffect(() => {
    walletService.getRamos()
      .then(res => setRamos(res.ramos ?? []))
      .catch(() => setRamos([]));
  }, []);

  // health del sistema
  const [health, setHealth]               = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  useEffect(() => {
    setHealthLoading(true);
    walletService.getHealth()
      .then(setHealth)
      .catch(() => setHealth(null))
      .finally(() => setHealthLoading(false));
  }, []);

  // detalle
  const [selected, setSelected]           = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pushingId, setPushingId]         = useState(null);
  const [simulatingEvent, setSimulatingEvent] = useState(null);
  const [reissueProvider, setReissueProvider] = useState('apple');

  // emisión
  const [issueOpen, setIssueOpen]           = useState(false);
  const [issuePolicy, setIssuePolicy]       = useState(null);
  const [issueProvider, setIssueProvider]   = useState('apple');
  const [issuing, setIssuing]               = useState(false);
  const [issueSearch, setIssueSearch]       = useState('');
  const [issueResults, setIssueResults]     = useState([]);
  const [issueSearching, setIssueSearching] = useState(false);
  const issueDebounce = useRef(null);

  // ─── carga principal ────────────────────────────────────────────────────

  const load = useCallback(async ({ background = false } = {}) => {
    // background=true → refresca sin vaciar la tabla (sin pantallazo)
    if (background) setRefreshing(true);
    else            setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search)       params.search = search;
      if (typeFilter)   params.insurance_type = typeFilter;
      if (statusFilter) params.coverage_status = statusFilter;
      const res = await walletService.listPasses(params);
      setPasses(res.data ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      toast({ title: 'Error cargando passes', description: err.message, variant: 'destructive' });
    } finally {
      if (background) setRefreshing(false);
      else            setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter, toast]);

  useEffect(() => { load(); }, [load]);

  // ─── búsqueda en issue dialog ────────────────────────────────────────────

  useEffect(() => {
    if (!issueOpen) return;
    clearTimeout(issueDebounce.current);
    if (!issueSearch.trim()) { setIssueResults([]); return; }

    issueDebounce.current = setTimeout(async () => {
      setIssueSearching(true);
      try {
        const res = await walletService.searchPolicies(issueSearch.trim());
        setIssueResults(res.data ?? []);
      } catch {
        setIssueResults([]);
      } finally {
        setIssueSearching(false);
      }
    }, 350);

    return () => clearTimeout(issueDebounce.current);
  }, [issueSearch, issueOpen]);

  // ─── detalle ────────────────────────────────────────────────────────────

  const [cuotas, setCuotas] = useState(null);

  const openDetail = async (pass) => {
    setSelected(pass);
    setReissueProvider(pass.wallet?.serial_number?.startsWith('GW-') ? 'google' : 'apple');
    setCuotas(null);
    setDetailLoading(true);
    try {
      const detail = await walletService.getPass(policyId(pass));
      setSelected(detail);
      setReissueProvider(detail.wallet?.serial_number?.startsWith('GW-') ? 'google' : 'apple');
      try {
        setCuotas(await walletService.getCuotas(policyId(pass)));
      } catch { /* cuotas opcionales */ }
    } catch {
      // mantener resumen si falla el detalle
    } finally {
      setDetailLoading(false);
    }
  };

  // ─── acciones ────────────────────────────────────────────────────────────

  const handlePush = async (pid) => {
    setPushingId(pid);
    try {
      const res = await walletService.sendPush(pid, 'manual desde tadmin');
      toast({ title: res.ok ? `Push enviado a ${res.devices_notified} dispositivos` : (res.reason ?? 'Sin dispositivos') });
    } catch (err) {
      toast({ title: 'Error en push', description: err.message, variant: 'destructive' });
    } finally {
      setPushingId(null);
    }
  };

  const handleSimulateEvent = async (pid, eventType) => {
    setSimulatingEvent(eventType);
    try {
      const res = await walletService.simulateEvent(eventType, pid);
      if (res.ok) {
        // El estado nuevo viene en la respuesta (display_state), sin condición de
        // carrera. Además re-consultamos el detalle para refrescar la vista previa.
        const nuevoEstado = res.display_state;
        const detail = await walletService.getPass(pid);
        setSelected(detail);
        load({ background: true }); // refresca también la lista (estado del pase)
        toast({
          title: '✅ Cambio enviado al wallet',
          description: nuevoEstado
            ? `El pase ahora está en "${nuevoEstado}". Revisá tu Google Wallet.`
            : 'El cambio se envió correctamente. Revisá tu Google Wallet.',
        });
      } else {
        toast({ title: 'No se pudo enviar el cambio', description: res.error || `Error ${res.status ?? ''}`, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error simulando evento', description: err.message, variant: 'destructive' });
    } finally {
      setSimulatingEvent(null);
    }
  };

  const handleRevoke = async (pid) => {
    if (!window.confirm('¿Revocar este pass? Esta acción no se puede deshacer.')) return;
    try {
      await walletService.revokePass(pid);
      toast({ title: 'Pass revocado' });
      setSelected(null);
      load({ background: true });
    } catch (err) {
      toast({ title: 'Error revocando pass', description: err.message, variant: 'destructive' });
    }
  };

  const handleDownload = async (serial) => {
    if (!serial) { toast({ title: 'Sin serial disponible', variant: 'destructive' }); return; }
    // La pestaña se abre ya (dentro del clic, para que el navegador no la
    // bloquee) y se apunta a la URL cuando llega: /download exige sesión de
    // Clerk y una pestaña nueva no puede mandar el header Authorization.
    const pestana = window.open('', '_blank');
    try {
      const { url } = await walletService.getDownloadUrl(serial);
      if (pestana) pestana.location.href = url;
      else window.location.assign(url);
    } catch (err) {
      if (pestana) pestana.close();
      toast({ title: 'Error descargando pass', description: err.message, variant: 'destructive' });
    }
  };

  const handleReissue = async () => {
    if (!selected) return;
    setIssuing(true);
    try {
      const pid = policyId(selected);
      const res = await walletService.issuePass(pid, reissueProvider);
      toast({ title: 'Pass re-emitido', description: `Serial: ${res.serial_number}` });
      setSelected(null);
      load({ background: true });
      if (res.pkpass_url) window.open(res.pkpass_url, '_blank');
      else if (res.google_jwt) window.open(`https://pay.google.com/gp/v/save/${res.google_jwt}`, '_blank');
    } catch (err) {
      toast({ title: 'Error re-emitiendo', description: err.message, variant: 'destructive' });
    } finally {
      setIssuing(false);
    }
  };

  const handleCopySerial = (serial) => {
    if (!serial) return;
    navigator.clipboard.writeText(serial);
    toast({ title: 'Serial copiado' });
  };

  const handleIssue = async (e) => {
    e.preventDefault();
    if (!issuePolicy) return;
    setIssuing(true);
    try {
      const res = await walletService.issuePass(issuePolicy.policy_id, issueProvider);
      toast({ title: `Pass ${issueProvider} emitido`, description: `Serial: ${res.serial_number}` });
      setIssueOpen(false);
      resetIssueDialog();
      load({ background: true });
      if (res.pkpass_url) window.open(res.pkpass_url, '_blank');
      else if (res.google_jwt) window.open(`https://pay.google.com/gp/v/save/${res.google_jwt}`, '_blank');
    } catch (err) {
      toast({ title: 'Error emitiendo pass', description: err.message, variant: 'destructive' });
    } finally {
      setIssuing(false);
    }
  };

  const handleGoogleJwt = async (pid) => {
    try {
      const res = await walletService.getGoogleJwt(pid);
      await navigator.clipboard.writeText(res.google_jwt);
      toast({ title: 'JWT copiado al portapapeles' });
    } catch (err) {
      toast({ title: 'Error obteniendo JWT', description: err.message, variant: 'destructive' });
    }
  };

  const handleOpenGoogleWallet = async (pid) => {
    try {
      const res = await walletService.getGoogleJwt(pid);
      window.open(`https://pay.google.com/gp/v/save/${res.google_jwt}`, '_blank');
    } catch (err) {
      toast({ title: 'Error abriendo Google Wallet', description: err.message, variant: 'destructive' });
    }
  };

  const resetIssueDialog = () => {
    setIssuePolicy(null);
    setIssueSearch('');
    setIssueResults([]);
    setIssueProvider('apple');
  };

  // ─── kpis ────────────────────────────────────────────────────────────────

  const installed   = passes.filter(p => p.pass_installed).length;
  const critical    = passes.filter(p => ['Pago rechazado', 'Suspendida'].includes(p.display_state)).length;
  const withDevices = passes.filter(p => p.devices_count > 0).length;

  // ─── health helpers ──────────────────────────────────────────────────────

  const certExpiry        = health?.checks?.pkpass_signing?.cert_expiry;
  const certDaysRemaining = certExpiry
    ? Math.floor((new Date(certExpiry) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const apnsOk         = health?.checks?.apns_jwt?.ok !== false;
  const healthDegraded = health?.status === 'degraded' || (certDaysRemaining != null && certDaysRemaining < 30);

  // ─── render ──────────────────────────────────────────────────────────────

  return (
    <div className="relative isolate space-y-6 p-6">
      <GlassAura />

      {/* header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-[#007D6E]/15 text-[#007D6E] ring-1 ring-[#007D6E]/20 backdrop-blur-xl">
            <Wallet className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Digital Wallet</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Passes Apple Wallet y Google Wallet
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/wallet/transmisiones')}>
            <Bell className="h-4 w-4 mr-2" />
            Transmisiones
          </Button>
          <Button onClick={() => { resetIssueDialog(); setIssueOpen(true); }} className="bg-[#007D6E] hover:bg-[#006558]">
            <Wallet className="h-4 w-4 mr-2" />
            Emitir pass
          </Button>
        </div>
      </div>

      {/* health panel */}
      <GlassCard className={healthDegraded ? 'ring-red-300/60' : ''}>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Server className={`h-4 w-4 ${healthDegraded ? 'text-red-500' : 'text-emerald-500'}`} />
            <p className="text-sm font-medium">Estado del sistema</p>
            {healthLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          </div>
          {health ? (
            <div className="flex flex-wrap gap-8 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Certificado Apple</p>
                <p className={`font-semibold ${(certDaysRemaining != null && certDaysRemaining < 30) ? 'text-[var(--yool-error)]' : 'text-[var(--yool-success)]'}`}>
                  {certDaysRemaining != null ? `${certDaysRemaining} días restantes` : 'OK'}
                </p>
                {certExpiry && (
                  <p className="text-[10px] text-muted-foreground">Expira {fmt(certExpiry)}</p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">APNS</p>
                <p className={`font-semibold ${apnsOk ? 'text-[var(--yool-success)]' : 'text-[var(--yool-error)]'}`}>
                  {apnsOk ? 'OK' : 'Error'}
                </p>
                {health.checks?.apns_jwt?.error && (
                  <p className="text-[10px] text-red-500">{health.checks.apns_jwt.error}</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No disponible</p>
          )}
        </div>
      </GlassCard>

      {/* kpis */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total passes',     value: total,       icon: Wallet,      tint: 'text-[#007D6E]' },
          { label: 'Instalados',       value: installed,   icon: CheckCircle, tint: 'text-[var(--yool-success)]' },
          { label: 'Con dispositivos', value: withDevices, icon: Smartphone,  tint: 'text-sky-600' },
          { label: 'Estado crítico',   value: critical,    icon: AlertCircle, tint: 'text-[var(--yool-error)]' },
        ].map(({ label, value, icon, tint }) => (
          <GlassKpi key={label} label={label} value={value} icon={icon} tint={tint} loading={loading} />
        ))}
      </div>

      {/* filtros */}
      <GlassCard>
        <div className="flex gap-3 flex-wrap p-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar titular…"
            className="pl-9"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={typeFilter || 'all'} onValueChange={v => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Ramo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los ramos</SelectItem>
            {ramos.map(t => (
              <SelectItem key={t} value={t}>{ramoLabel(t)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={() => load({ background: true })} disabled={loading || refreshing}>
          <RefreshCw className={`h-4 w-4 ${loading || refreshing ? 'animate-spin' : ''}`} />
        </Button>
        </div>
      </GlassCard>

      {/* tabla */}
      <GlassCard>
        <div className="p-0 overflow-hidden rounded-2xl">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">Cargando…</div>
          ) : passes.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">Sin resultados</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="text-left px-4 py-3 font-medium">Titular</th>
                    <th className="text-left px-4 py-3 font-medium">Póliza</th>
                    <th className="text-left px-4 py-3 font-medium">Certificado</th>
                    <th className="text-left px-4 py-3 font-medium">Ramo</th>
                    <th className="text-left px-4 py-3 font-medium">Estado</th>
                    <th className="text-center px-4 py-3 font-medium">Dispositivos</th>
                    <th className="text-left px-4 py-3 font-medium">Último push</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {passes.map(p => {
                    const TypeIcon = TYPE_ICON[p.insurance_type] ?? Shield;
                    return (
                      <tr
                        key={policyId(p)}
                        className="border-b last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                        onClick={() => openDetail(p)}
                      >
                        <td className="px-4 py-3 font-medium">{p.holder_name}</td>
                        <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{p.policy_number}</td>
                        <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{p.certificate_number || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <TypeIcon className="h-3.5 w-3.5" />
                            {ramoLabel(p.insurance_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3"><StatePill state={p.display_state} /></td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-mono text-xs ${p.devices_count > 0 ? 'text-[var(--yool-success)] font-semibold' : 'text-muted-foreground'}`}>
                            {p.devices_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(p.last_push)}</td>
                        <td className="px-4 py-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </GlassCard>

      {/* paginación */}
      {total > LIMIT && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{total} resultados · página {page}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
          </div>
        </div>
      )}

      {/* ── DETAIL DIALOG ──────────────────────────────────────────────────── */}
      <Dialog open={!!selected} onOpenChange={open => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl border-white/40 dark:border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[#007D6E]" />
              {selected?.policy?.holder_name ?? selected?.holder_name ?? 'Pass'}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Cargando detalle…
            </div>
          ) : selected && (
            <div>
              {/* barra de acciones */}
              <div className="space-y-2 pb-3 border-b">
                {/* fila 1: acciones generales */}
                <div className="flex flex-wrap gap-2">
                  <Select value={reissueProvider} onValueChange={setReissueProvider}>
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apple">Apple Wallet</SelectItem>
                      <SelectItem value="google">Google Wallet</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleReissue}
                    disabled={issuing}
                  >
                    {issuing
                      ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Emitiendo…</>
                      : <><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Re-emitir</>
                    }
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopySerial(selected.wallet?.serial_number)}
                    disabled={!selected.wallet?.serial_number}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1.5" />
                    Serial
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleRevoke(policyId(selected))}>
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Revocar
                  </Button>
                </div>

                {/* fila 2: acciones según provider seleccionado */}
                {reissueProvider === 'google' ? (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleOpenGoogleWallet(policyId(selected))}>
                      <Wallet className="h-3.5 w-3.5 mr-1.5" />
                      Abrir en Google Wallet
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleGoogleJwt(policyId(selected))}>
                      <Copy className="h-3.5 w-3.5 mr-1.5" />
                      Copiar JWT
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(selected.wallet?.serial_number)}
                      disabled={!selected.wallet?.serial_number}
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Descargar .pkpass
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePush(policyId(selected))}
                      disabled={pushingId === policyId(selected) || !(selected.wallet?.installed_devices > 0 || selected.devices_count > 0)}
                    >
                      {pushingId === policyId(selected)
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <Bell className="h-3.5 w-3.5 mr-1.5" />}
                      Push APNS
                    </Button>
                  </div>
                )}
              </div>

              {/* simulate events — solo cuando REACT_APP_ENABLE_DEV_TOOLS=true
                  (setear esa env solo en el deployment de pruebas, no en prod) */}
              {process.env.REACT_APP_ENABLE_DEV_TOOLS === 'true' && (
                <div className="mt-2 px-0">
                  <p className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold mb-1.5">Simular evento (dev)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { label: 'Pago OK',        event: 'payment.paid' },
                      { label: 'Pago rechazado', event: 'payment.rejected' },
                      { label: 'Suspender',      event: 'coverage.suspended' },
                      { label: 'Siniestro',      event: 'claim.opened' },
                    ].map(({ label, event }) => (
                      <Button
                        key={event}
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 border-dashed border-amber-300 text-amber-700 hover:bg-amber-50"
                        disabled={simulatingEvent !== null}
                        onClick={() => handleSimulateEvent(policyId(selected), event)}
                      >
                        {simulatingEvent === event && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* tabs */}
              <Tabs defaultValue="preview" className="mt-3">
                <TabsList>
                  <TabsTrigger value="preview">Vista previa</TabsTrigger>
                  <TabsTrigger value="info">Información</TabsTrigger>
                  <TabsTrigger value="back">Reverso</TabsTrigger>
                </TabsList>

                {/* ── Tab 1: Vista previa ── */}
                <TabsContent value="preview">
                  <div className="max-h-[58vh] overflow-y-auto space-y-3 pr-1">
                    <div className="flex justify-center rounded-xl bg-slate-900 py-5 px-4">
                      <PassCard
                        policy={selected.policy ?? selected}
                        wallet={selected.wallet}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <StatePill state={selected.policy?.display_state ?? selected.display_state} />
                      <span className="text-xs text-muted-foreground">
                        versión {selected.wallet?.active_version ?? selected.version ?? '—'}
                      </span>
                    </div>
                  </div>
                </TabsContent>

                {/* ── Tab 2: Información ── */}
                <TabsContent value="info">
                  <div className="max-h-[58vh] overflow-y-auto space-y-4 pr-1">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {[
                        ['Titular',        selected.policy?.holder_name    ?? selected.holder_name],
                        ['RUT',            selected.policy?.holder_rut     ?? selected.holder_rut    ?? '—'],
                        ['Certificado',    selected.policy?.certificate_number],
                        ['N° Póliza',      selected.policy?.policy_number  ?? selected.policy_number],
                        ['Tipo',           ramoLabel(selected.policy?.insurance_type ?? selected.insurance_type)],
                        ['Compañía',       selected.policy?.insurance_company],
                        ['Plan',           selected.policy?.insurance_plan],
                        ['Vigencia desde', fmt(selected.policy?.start_date)],
                        ['Vigencia hasta', fmt(selected.policy?.end_date)],
                        ['Cobertura',      selected.policy?.coverage_status],
                        ['Pago',           selected.policy?.payment_status],
                        ['Siniestro',      selected.policy?.claim_status],
                        ['Dispositivos',   selected.wallet?.installed_devices ?? selected.devices_count ?? 0],
                        ['Serial',         selected.wallet?.serial_number],
                        ['Último pass',    fmt(selected.wallet?.last_pass_generated ?? selected.last_update)],
                        ['Último push',    fmt(selected.wallet?.last_push_sent ?? selected.last_push)],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
                          <p className="font-medium break-all text-sm">{value ?? '—'}</p>
                        </div>
                      ))}
                    </div>

                    {/* plan de pagos / cuotas */}
                    {cuotas?.plan && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Plan de pagos — {cuotas.plan.paid} de {cuotas.plan.total} pagadas
                          {cuotas.plan.failed > 0 && ` · ${cuotas.plan.failed} fallidas`}
                        </p>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b bg-slate-50 dark:bg-slate-800/40 text-muted-foreground">
                                <th className="text-left px-3 py-2 font-medium">#</th>
                                <th className="text-left px-3 py-2 font-medium">Fecha</th>
                                <th className="text-left px-3 py-2 font-medium">Monto</th>
                                <th className="text-left px-3 py-2 font-medium">Estado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cuotas.cuotas.map((c) => (
                                <tr key={c.n} className="border-b last:border-0">
                                  <td className="px-3 py-2 font-mono text-muted-foreground">{c.n}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{fmt(c.charge_date)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{cuotaMoney(c)}</td>
                                  <td className="px-3 py-2"><CuotaBadge status={c.status} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* event log */}
                    {selected.events?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Últimos eventos ({Math.min(selected.events.length, 20)})
                        </p>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b bg-slate-50 dark:bg-slate-800/40 text-muted-foreground">
                                <th className="text-left px-3 py-2 font-medium">Fecha/hora</th>
                                <th className="text-left px-3 py-2 font-medium">Evento</th>
                                <th className="text-left px-3 py-2 font-medium">Fuente</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selected.events.slice(0, 20).map(ev => (
                                <tr key={ev.id} className="border-b last:border-0">
                                  <td className="px-3 py-2 font-mono text-muted-foreground whitespace-nowrap">
                                    {fmtDatetime(ev.created_at)}
                                  </td>
                                  <td className="px-3 py-2 font-medium">{ev.event_type}</td>
                                  <td className="px-3 py-2 text-muted-foreground">{ev.event_source ?? '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* ── Tab 3: Reverso — BackConfigEditor ── */}
                <TabsContent value="back">
                  <div className="max-h-[58vh] overflow-y-auto pr-1">
                    <BackConfigEditor
                      polizaColectivaId={selected?.poliza_colectiva_id ?? null}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── ISSUE DIALOG ───────────────────────────────────────────────────── */}
      <Dialog open={issueOpen} onOpenChange={open => { if (!open) { setIssueOpen(false); resetIssueDialog(); } }}>
        <DialogContent className="max-w-md bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl border-white/40 dark:border-white/10">
          <DialogHeader>
            <DialogTitle>Emitir pass</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleIssue} className="space-y-4">

            <div>
              <label className="text-sm font-medium mb-1.5 block">Buscar póliza</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nombre del titular…"
                  className="pl-9"
                  value={issueSearch}
                  onChange={e => { setIssueSearch(e.target.value); setIssuePolicy(null); }}
                  autoFocus
                />
                {issueSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {issueResults.length > 0 && !issuePolicy && (
                <div className="mt-1 border rounded-lg overflow-hidden max-h-52 overflow-y-auto">
                  {issueResults.map(p => {
                    const TypeIcon = TYPE_ICON[p.insurance_type] ?? Shield;
                    return (
                      <button
                        key={p.policy_id}
                        type="button"
                        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800 border-b last:border-0 transition-colors"
                        onClick={() => { setIssuePolicy(p); setIssueSearch(''); setIssueResults([]); }}
                      >
                        <div>
                          <p className="text-sm font-medium">{p.holder_name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{p.policy_number}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <TypeIcon className="h-3.5 w-3.5" />
                            {ramoLabel(p.insurance_type)}
                          </span>
                          {p.pass_installed && (
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" title="Ya tiene pass" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {issueSearch.trim() && !issueSearching && issueResults.length === 0 && !issuePolicy && (
                <p className="text-xs text-muted-foreground mt-2 px-1">Sin resultados para "{issueSearch}"</p>
              )}
            </div>

            {issuePolicy && (
              <div className="rounded-lg border bg-slate-50 dark:bg-slate-800 px-3 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{issuePolicy.holder_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {issuePolicy.policy_number} · {ramoLabel(issuePolicy.insurance_type)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {issuePolicy.pass_installed && (
                    <span className="text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 font-medium">
                      Ya tiene pass
                    </span>
                  )}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground text-xs underline"
                    onClick={() => setIssuePolicy(null)}
                  >
                    cambiar
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-1.5 block">Plataforma</label>
              <Select value={issueProvider} onValueChange={setIssueProvider}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apple">Apple Wallet</SelectItem>
                  <SelectItem value="google">Google Wallet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => { setIssueOpen(false); resetIssueDialog(); }}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={issuing || !issuePolicy}
                className="bg-[#007D6E] hover:bg-[#006558]"
              >
                {issuing ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Emitiendo…</> : 'Emitir'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
